// src/services/data/queryKeys.ts
/**
 * Unified Query Key Factory
 * 
 * Centralized query keys for all metric data fetching.
 * Enables cache sharing between:
 * - Token/Sector data providers
 * - Assistant chart data
 * - Direct hook consumers
 */

import type { 
  EntityIdentifier, 
  MetricRequest,
} from './types';
import { normalizeMetricKey, normalizeEntities } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Token Data Keys
// ─────────────────────────────────────────────────────────────────────────────

export const tokenDataKeys = {
  all: ['tokenData'] as const,
  
  /** Current metric values for tokens */
  current: (params: {
    tickers: string[];
    metrics: MetricRequest[];
  }) => [
    ...tokenDataKeys.all,
    'current',
    {
      tickers: [...params.tickers].sort(),
      metrics: params.metrics.map(normalizeMetricKey).sort((a, b) => a.key.localeCompare(b.key)),
    },
  ] as const,
  
  /** Historical time series for tokens */
  history: (params: {
    tickers: string[];
    metricKey: string;
    isRatio?: boolean;
  }) => [
    ...tokenDataKeys.all,
    'history',
    {
      tickers: [...params.tickers].sort(),
      metricKey: params.metricKey,
      isRatio: params.isRatio ?? params.metricKey.startsWith('ratio:'),
    },
  ] as const,
  
  /** Change values (percent change over window) */
  changes: (params: {
    tickers: string[];
    metricKey: string;
    window: string;
  }) => [
    ...tokenDataKeys.all,
    'changes',
    {
      tickers: [...params.tickers].sort(),
      metricKey: params.metricKey,
      window: params.window,
    },
  ] as const,
  
  /** Rollup values (aggregated over window) */
  rollups: (params: {
    tickers: string[];
    metricKey: string;
    window: string;
  }) => [
    ...tokenDataKeys.all,
    'rollups',
    {
      tickers: [...params.tickers].sort(),
      metricKey: params.metricKey,
      window: params.window,
    },
  ] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// Category/Sector Data Keys
// ─────────────────────────────────────────────────────────────────────────────

export const categoryDataKeys = {
  all: ['categoryData'] as const,
  
  /** Category ID lookup cache */
  idMap: () => [...categoryDataKeys.all, 'idMap'] as const,
  
  /** Current metric values for categories */
  current: (params: {
    categoryIds: number[];
  }) => [
    ...categoryDataKeys.all,
    'current',
    {
      categoryIds: [...params.categoryIds].sort((a, b) => a - b),
    },
  ] as const,
  
  /** Historical time series for categories */
  history: (params: {
    categoryId: number;
    metricKeys: string[];
  }) => [
    ...categoryDataKeys.all,
    'history',
    {
      categoryId: params.categoryId,
      metricKeys: [...params.metricKeys].sort(),
    },
  ] as const,
  
  /** Change values for categories */
  changes: (params: {
    categoryIds: number[];
  }) => [
    ...categoryDataKeys.all,
    'changes',
    {
      categoryIds: params.categoryIds.length > 0 ? [...params.categoryIds].sort((a, b) => a - b) : 'all',
    },
  ] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// Unified Entity Keys (entity-agnostic)
// ─────────────────────────────────────────────────────────────────────────────

export const entityDataKeys = {
  all: ['entityData'] as const,
  
  /** 
   * Unified current metric values.
   * Use this when you want to fetch metrics for mixed entity types.
   */
  current: (params: {
    entities: EntityIdentifier[];
    metrics: MetricRequest[];
  }) => [
    ...entityDataKeys.all,
    'current',
    {
      entities: normalizeEntities(params.entities).map(e => `${e.type}:${e.id}`),
      metrics: params.metrics.map(normalizeMetricKey).sort((a, b) => a.key.localeCompare(b.key)),
    },
  ] as const,
  
  /**
   * Unified historical data.
   */
  history: (params: {
    entities: EntityIdentifier[];
    metricKey: string;
    transform?: 'none' | 'change' | 'rollup';
    window?: string;
  }) => [
    ...entityDataKeys.all,
    'history',
    {
      entities: normalizeEntities(params.entities).map(e => `${e.type}:${e.id}`),
      metricKey: params.metricKey,
      transform: params.transform ?? 'none',
      window: params.window ?? null,
    },
  ] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// Assistant Chart Keys (uses above keys for cache sharing)
// ─────────────────────────────────────────────────────────────────────────────

export const assistantChartKeys = {
  all: ['assistantChart'] as const,
  
  /**
   * Chart-specific key that includes chart ID for React Query tracking,
   * but delegates actual data fetching to shared keys above.
   */
  chart: (chartId: string) => [
    ...assistantChartKeys.all,
    chartId,
  ] as const,
  
  /**
   * For stat cards (pre-computed, no fetching needed)
   */
  statCard: (chartId: string) => [
    ...assistantChartKeys.all,
    'statCard',
    chartId,
  ] as const,
};

